"use client";

import { getTranslation } from "@/i18n/get-translation";
import Link from "next/link";
import { useEffect, useRef, useState, type ComponentType } from "react";

interface IBanner {
  locale: string;
}
export default function Banner({ locale }: IBanner) {
  const t = getTranslation(locale, "banner");
  const sectionRef = useRef<HTMLElement>(null);
  const [PlayerComp, setPlayerComp] = useState<ComponentType<any> | null>(null);
  const [animData, setAnimData] = useState<any>(null);

  // Only load Lottie when banner is near viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          import("@lottiefiles/react-lottie-player").then(mod => {
            setPlayerComp(() => mod.Player);
          });
          import("./animations/bottom.json").then(mod => {
            setAnimData(mod.default);
          });
          observer.unobserve(el);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="px-20 max-md:px-0">
      <div className="relative text-center py-14 px-12 max-md:px-7 bg-primary-500 max-w-325.5 mx-auto rounded-3xl max-md:max-w-full max-md:rounded-none banner-glow">
        <div className="flex text-left items-center justify-between">
          <div className="basis-[60%] max-md:basis-full">
            <div className="max-w-full">
              <h2 className="xl:text-[3rem] lg:text-[2.5rem] md:text-[2.2rem] text-[1.88rem] leading-[1.1] font-medium text-primary-800">
                {t("title")}
              </h2>
              <p className="mt-6 text-primary-600 text-xl font-light">
                {t("desc")}
              </p>
              <div className="flex mt-6">
                <Link
                  href={`/${locale}/request-demo`}
                  className="whitespace-nowrap arrowBtn text-white bg-(image:--primary-gradient) rounded-full pl-5 pr-2 py-2 leading-4 flex items-center cta-shimmer"
                >
                  {t("requestADemo")}
                  <div className="relative ml-2">
                    <div className="bg-primary-400 text-white rounded-full w-10 h-10 opacity-[0.24]"></div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 10 18"
                      className="w-2.5 text-white font-bold absolute left-2/4 top-2/4 transform-[translate(-50%,-50%)]"
                    >
                      <path stroke="currentColor" d="M1 16.5 8 9 1 1.5"></path>
                    </svg>
                  </div>
                </Link>
                <Link
                  href={`/${locale}/contact-us`}
                  className="whitespace-nowrap arrowBtn text-primary-700 font-medium rounded-full px-5 py-2 leading-4 flex items-center"
                >
                  {t("contactSales")}
                  <div className="relative ml-2">
                    <div className="bg-primary-400 rounded-full w-10 h-10 opacity-[0.24]"></div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 10 18"
                      className="w-2.5 absolute left-2/4 top-2/4 transform-[translate(-50%,-50%)]"
                    >
                      <path stroke="currentColor" d="M1 16.5 8 9 1 1.5"></path>
                    </svg>
                  </div>
                </Link>
              </div>
            </div>
          </div>
          <div className="basis-{40%} max-md:basis-full max-md:hidden">
            {PlayerComp && animData && <PlayerComp
              autoplay
              loop
              src={animData}
              className="ml-auto"
            />}
          </div>
        </div>
      </div>
    </section>
  );
}
